/**
 * Where an app that cannot open a hub sends the person (card `t_bfe0edc9`).
 *
 * Measured on this box in the Electron dev app, signed in, hub reachable on the
 * LAN, nothing on the device that opens it:
 *
 *   1. the connect says "No credential for this hub — join or adopt it";
 *   2. the app paints a surface whose own subtitle says it is for guests,
 *     contractors and keyboards-less devices;
 *   3. the route falls to `/home`, and a hub tab goes on rendering the hub's
 *     shell with nothing behind it.
 *
 * Two of those are the same defect: the app answers the owner with the *guest*
 * door. The standing rule is the opposite — "the adoption should happen on the
 * Local page" — and the self-install slice exists so a box the owner runs needs
 * no code at all. So the decision below is: **whose** credential is missing, and
 * where does that person's own way in live?
 *
 *   - A **Torik account**, and the hub at a private address: the Local page. On
 *     that page the account IS the credential (adopt an unclaimed hub, else
 *     open the hub the account already holds) — and the page carries the hub's
 *     own facts next to the button (plan `LOCAL-FIRST-CONNECT.md` §2/D3).
 *   - A **panel** (no account, no keyboard) — whatever went wrong: pairing is how
 *     a panel gets in (`/pair`, `views/PanelPairing.vue`), and a refused
 *     credential is exactly the state that flow repairs.
 *   - Any other device, and the refusal that means "this hub wants a new
 *     credential" (`credential-refused`): the **sign-in screen** (`/login`),
 *     whose ways in are the Torik account and — for a person standing at the
 *     box — the hub's own local password. There is no code anywhere to scan
 *     (card `t_2f0c4a91`).
 *   - Anything else — a guest with nothing to sign in with, a hub nobody can
 *     place on this network: the launcher (`/home`), which lists the device's
 *     servers and carries the other ways in. Today's behaviour, kept.
 *
 * The decision is a pure function of a handful of facts so it can be tested
 * without a DOM, the same shape as `utils/localRescan.ts`. Every surface that
 * used to bounce or land a person somewhere wrong asks *this* module instead:
 * `stores/auth.ts` (a refused device credential), `views/TabConnect.vue` (a
 * failed tab connect), `views/LocalServers.vue` (a failed Open on the page
 * itself) and `router/index.ts` (the guard's credential bounce).
 *
 * The second half of the same card lives here too: a hub's own pages must never
 * render their shell without a credential (`mayRenderHubShell`) — the app shows
 * the needs-join state instead of a shell with nothing behind it.
 */
import { isPrivateOrLocalHostname } from '../config/runtime';
import type { LocalRouteRefusal } from '../services/hubLocalFirst';
import type { CloudSessionRefusal } from '../services/cloudSessionRefusal';

/**
 * Why this app cannot open the hub. `LocalRouteRefusal` is the LAN route's own
 * vocabulary (`services/hubLocalFirst.ts` — one name per way a local door stays
 * shut); `CloudSessionRefusal` is the cloud's (`services/cloudSessionRefusal.ts` —
 * `remote_access_not_licensed`: the hub is set to local access only, so the tunnel
 * is not a route to it either); the two extra names are the ones the *device*
 * contributes.
 */
export type HubOpenFailure =
  | LocalRouteRefusal
  | CloudSessionRefusal
  /** The hub refused the credential this device held, so it was dropped. */
  | 'credential-refused'
  /** The device holds nothing for this hub (yet). */
  | 'no-credential';

/** Every failure this module knows a sentence for, in one place. */
export const HUB_OPEN_FAILURES: readonly HubOpenFailure[] = [
  'credential-refused',
  'no-credential',
  'no-cloud-session',
  'no-lan-address',
  'no-account-identity',
  'unreachable',
  'permission-held',
  'not-this-hub',
  'not-proven',
  'cannot-prove',
  'refused',
  // The one the *cloud* names (plan `FLOOR-PLAN-AND-PLAN-EDITOR.md` §3.2/D3): a
  // hub the tunnel refuses because its terms are local-only. It is last because it
  // is the last door a connect tries — and it is a failure of this vocabulary, not
  // of the LAN route's, so the sentence it maps to (`hubOpenMessageKey`) is the
  // whole point of carrying it.
  'remote_access_not_licensed',
] as const;

/** The surface that owns the person's way in. */
export type HubOpenSurface = 'local' | 'signin' | 'pair' | 'launcher';

/** The route each surface lives at. */
export const HUB_OPEN_PATHS: Record<HubOpenSurface, string> = {
  local: '/local',
  signin: '/login',
  pair: '/pair',
  launcher: '/home',
};

export interface HubOpenAddress {
  ip: string;
  port?: number;
}

export interface HubOpenFacts {
  failure: HubOpenFailure;
  /** A Torik account is signed in on this device (`authStore.isCloudAuthenticated`). */
  accountSignedIn: boolean;
  /** The hub's own name, when the app knows it — the surface must name it. */
  hubName?: string | null;
  /** The address the app was going to dial, when it has one. */
  address?: HubOpenAddress | null;
  /** This device is a configured panel/kiosk (`config/panelDevice.ts`): no account, no keyboard. */
  panelDevice?: boolean;
}

export interface HubOpenPlan {
  surface: HubOpenSurface;
  /** Where to navigate — the Local page carries the hub and the reason. */
  route: { path: string; query: Record<string, string> };
  /** The i18n key of the sentence the destination paints (`hub.open.failure.*`). */
  messageKey: string;
}

/** Is this address one of the device's own network (a private or local address)? */
export function isOwnNetworkAddress(ip: unknown): boolean {
  return typeof ip === 'string' && ip.trim() !== '' && isPrivateOrLocalHostname(ip.trim());
}

/**
 * The i18n key for one failure — one name, one sentence, in both locales.
 *
 * The refusal's own name keeps its hyphens (`LocalRouteRefusal` is written that
 * way, and the query carries the name as it stands); the *key segment* is
 * spelled the way every key path in this repo is spelled — `[A-Za-z0-9_]`,
 * `KEY_SHAPE` in `locales/localeAudit.ts`, which the guard there checks
 * allowlisted entries against too. One place does the translation, this one.
 */
export function hubOpenMessageKey(failure: HubOpenFailure): string {
  const known = HUB_OPEN_FAILURES.includes(failure) ? failure : 'no-credential';
  return `hub.open.failure.${known.replace(/-/g, '_')}`;
}

/** A catalogue lookup, as this sentence needs one (`useI18n().t`). */
export type Translate = (key: string, named?: Record<string, unknown>) => string;

/**
 * The failure, said in words — **one** place said, so a person reads the same
 * sentence however it reaches them.
 *
 * The two facts the sentence can name may be missing, and the fallbacks are the
 * ones the notice surfaces already print (`components/server/FindLocalServersModal.vue`):
 * `enroll.hub.unknown` for a hub the app has no name for, and `—` for an address
 * it never had. Whitespace counts as missing — a query can carry `' '` and a
 * blank name must not become a blank sentence.
 *
 * It exists because a notice is not the only way the sentence is delivered: a
 * push tap whose destination will not paint one asks for it here instead
 * (`services/pushDeepLink.ts`), and two copies of this wording would be two
 * different things said about one failure.
 */
export function hubOpenFailureSentence(
  messageKey: string,
  hubName: string | null | undefined,
  address: string | null | undefined,
  t: Translate,
): string {
  return t(messageKey, {
    hub: hubName?.trim() || t('enroll.hub.unknown'),
    address: address?.trim() || '—',
  });
}

/** `host:port`, the spelling the Local page's cards and the connect use. */
export function formatHubOpenAddress(address?: HubOpenAddress | null): string | null {
  if (!address || !isOwnNetworkAddress(address.ip) || !Number.isFinite(Number(address.port))) return null;
  return `${address.ip}:${Number(address.port)}`;
}

/** The hub's name as a surface can paint it, or `null` when the app knows none. */
function namedHub(facts: HubOpenFacts): string | null {
  const name = typeof facts.hubName === 'string' ? facts.hubName.trim() : '';
  return name || null;
}

/**
 * Where does this person's own way in live?
 *
 * The owner case is the one that changed: an account signed in and a hub on this
 * device's own network means the Local page — whichever of its actions applies,
 * the hub's own state decides there, on screen, next to the hub's facts. A panel
 * keeps its own door (pairing, `/pair`), and a device with no account that is
 * told its credential was refused is sent to the sign-in screen, where the
 * account and the hub's local password are. Everything else keeps the launcher.
 */
export function resolveHubOpenPlan(facts: HubOpenFacts): HubOpenPlan {
  const messageKey = hubOpenMessageKey(facts.failure);

  // An owner, and a hub at an address on this device's own network: the Local
  // page, named and explained. The page is where adoption happens and where the
  // account opens a hub no credential was minted for.
  if (facts.accountSignedIn && !facts.panelDevice && isOwnNetworkAddress(facts.address?.ip)) {
    const query: Record<string, string> = { reason: facts.failure };
    const name = namedHub(facts);
    if (name) query.hub = name;
    const address = formatHubOpenAddress(facts.address);
    if (address) query.address = address;
    return { surface: 'local', route: { path: HUB_OPEN_PATHS.local, query }, messageKey };
  }

  // A panel: pairing is the panel's own way in, whatever went wrong. The panel
  // has no account to sign in with and no keyboard to type on — and the refusal
  // that brings it here is the one its pairing flow repairs.
  if (facts.panelDevice) {
    return { surface: 'pair', route: { path: HUB_OPEN_PATHS.pair, query: {} }, messageKey };
  }

  // A device whose credential the hub refused: the sign-in screen, which is the
  // only door left that mints one (an account, or this hub's local password).
  // Every other failure a guest can hit has nothing to sign in with either, so
  // those keep the launcher below — where they already went.
  if (facts.failure === 'credential-refused') {
    return { surface: 'signin', route: { path: HUB_OPEN_PATHS.signin, query: {} }, messageKey };
  }

  // Everything else: the launcher, which lists the device's servers.
  return { surface: 'launcher', route: { path: HUB_OPEN_PATHS.launcher, query: {} }, messageKey };
}

// ── The reading side: what the Local page paints when it is handed a failure ──

export interface HubOpenNotice {
  /** The hub the app could not open, when it had a name for it. */
  hubName: string | null;
  /** The address it tried, when it had one (`host:port`). */
  address: string | null;
  failure: HubOpenFailure;
  /** The sentence (`hub.open.failure.*`). */
  messageKey: string;
}

/** A failure name from an untrusted string (a query, a route param). */
export function asHubOpenFailure(value: unknown): HubOpenFailure | null {
  return typeof value === 'string' && (HUB_OPEN_FAILURES as readonly string[]).includes(value)
    ? (value as HubOpenFailure)
    : null;
}

/** The failure as a name, or the honest default when it is missing. */
function failureOrNothing(value: unknown): HubOpenFailure | null {
  return asHubOpenFailure(value);
}

/**
 * What a surface should paint for the query `resolveHubOpenPlan` wrote.
 *
 * Returns null for an absent/unreadable reason: a page nobody sent a failure to
 * paints nothing new, and a hand-written `?reason=` is not a sentence this app
 * will say (the reason is matched against the vocabulary, not interpolated).
 */
export function readHubOpenNotice(query: Record<string, unknown> | null | undefined): HubOpenNotice | null {
  const failure = failureOrNothing(query?.reason);
  if (!failure) return null;
  const hubName = typeof query?.hub === 'string' && query.hub.trim() ? query.hub.trim() : null;
  const address = typeof query?.address === 'string' && query.address.trim() ? query.address.trim() : null;
  return { hubName, address, failure, messageKey: hubOpenMessageKey(failure) };
}

// ── The hub's own shell, and the credential it needs ─────────────────────────

/** The hub this window is on, from its own record (the per-tab marker). */
export interface HubShellHub {
  ip: string;
  port: number;
  hubId?: string;
  name?: string;
}

export interface HubShellFacts {
  /** The route is one of the hub's own pages (`meta.requiresAuth`). */
  hubPage: boolean;
  /** This window holds a credential the hub will accept — a hub session, cloud access for this route, or a device credential. */
  hasCredential: boolean;
  /** The hub this window is on (`readTabActiveHub`), or null when it is on none. */
  hub: HubShellHub | null;
  /** A kiosk surface (panel/play/pairing): its own layout, its own gate. */
  kiosk: boolean;
}

/**
 * May the hub's own shell render for this route?
 *
 * `false` is the "no credential-less hub shell" half of card `t_bfe0edc9`: a
 * window that is on a hub of this network and holds nothing that opens it gets
 * the needs-join state, not the hub's navigation with dead pages behind it.
 * Everything this rule is not about (not a hub page, a kiosk, a credential that
 * exists) answers `true` — this never widens into a general gate.
 */
export function mayRenderHubShell(facts: HubShellFacts): boolean {
  if (!facts.hubPage || facts.kiosk) return true;
  if (facts.hasCredential) return true;
  return !isOwnNetworkAddress(facts.hub?.ip);
}
